import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ColorType, Icon, Label, LabelType, Scrollable } from '.';
import { Dashboard } from '../../assets/icons';
import { USER_ROLE, useAuth } from '../../hooks';
import { useCompany } from '../../hooks/useCompany';
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { GroupItem, RoutePath, checkIfSupportRoute, routes, sideBarMenus } from '../../routes';
import { getCurrentTheme } from '../../stores/selectors/theme.selector';
import { CompanyState } from '../../stores/types/company.interface';
import { getbrandname } from '../../utils/SearchParam.Utils';
import { CompanySelect } from './CompanySelect.component';
import { getIsItemSelected } from './Sidebar.util';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip.component';

export const AppSideBar = () => {
  const auth = useAuth();
  const { companyId, companies, isMultiViewActive } = useCompany();
  const {
    canViewFinances,
    canViewPricing,
    canViewPowerManagement,
    canViewVehicles,
    canViewRfidCards,
    canViewAccessRules,
  } = useCompanyPermissions(companyId && companyId.toUpperCase() !== 'ALL' ? companyId : undefined);
  const theme = useSelector(getCurrentTheme);
  const currentLocation = useLocation();
  const { navigateWithCompanyId } = useNavigateWithSearchParam();
  const { t } = useTranslation();
  const currentCompany = useMemo(() => {
    if (!companyId || isMultiViewActive) return undefined;
    return companies?.find((c) => c.id === companyId);
  }, [companies, companyId, isMultiViewActive]);

  const handleMenuClick = (appMenu: any) => {
    navigateWithCompanyId(`${appMenu.path}`, companyId, { replace: false });
  };

  const isPendingOnboarding = useMemo(() => {
    return currentCompany?.companyStateList?.includes(CompanyState.PENDING_ONBOARDING) ?? false;
  }, [currentCompany?.companyStateList]);

  const noCompanyMenu = [
    {
      items: [
        {
          header: '',
          path: RoutePath.OVERVIEW,
          icon: Dashboard,
          ignoreClick: true,
        },
      ],
    },
  ];

  const renderLogo = () => {
    return (
      <div
        tabIndex={isPendingOnboarding ? -1 : 0}
        role='button'
        onClick={() => {
          if (!isPendingOnboarding) {
            navigateWithCompanyId(`../${RoutePath.OVERVIEW}`, companyId, {
              replace: true,
            });
          }
        }}
        onKeyDown={(e: any) => {
          if (!isPendingOnboarding && (e.key === 'Enter' || e.key === ' ')) {
            navigateWithCompanyId(`../${RoutePath.OVERVIEW}`, companyId, {
              replace: true,
            });
          }
        }}
        className={`my-10 ${!isPendingOnboarding ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <img
          src={theme.networkLogo}
          width={176}
          height='auto'
          alt={t('icon_alt_brand_logo', { brandName: getbrandname() })}
        />
      </div>
    );
  };

  const renderHeader = (transKey: string) => (
    <div className='block pl-2 py-4 h-10 w-[200px]' key={transKey}>
      <Label text={t(transKey)} type={LabelType.LABEL_XS} color={ColorType.GREY6} />
    </div>
  );

  const getItemWithTooltip = (item: any, path: string, isDisabledDueToOnboarding: boolean) => {
    const title = t(routes.find((route) => route.path === path)?.name || '');
    const tooltipKey = isDisabledDueToOnboarding
      ? 'disabled_menu_tooltip_onboarding'
      : 'disabled_menu_tooltip';

    const labelText = (
      <Trans
        i18nKey={tooltipKey}
        values={{
          menu: title,
        }}
      />
    );
    return (
      <Tooltip
        key={`tooltip-${path}`}
        placement='top'
        tipOffset={{
          mainAxis: 0,
          crossAxis: 0,
        }}
      >
        <TooltipTrigger>{item}</TooltipTrigger>
        <TooltipContent>
          <Label
            text={labelText}
            type={LabelType.LABEL_S}
            color={ColorType.WHITE}
            className='w-40'
          />
        </TooltipContent>
      </Tooltip>
    );
  };

  const renderItem = (item: GroupItem) => {
    const title = t(routes.find((route) => route.path === item.path)?.name || '');
    return (
      <div
        tabIndex={0}
        role='button'
        onClick={() => (item.ignoreClick || item.isDisabled ? {} : handleMenuClick(item))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (!(item.ignoreClick || item.isDisabled)) {
              handleMenuClick(item);
            }
          }
        }}
        key={item.path}
        className={`${
          !item.isDisabled && 'hover:bg-grey1 cursor-pointer'
        } flex items-center w-[200px] h-10 rounded`}
        style={{
          backgroundColor: item.isSelected && !item.isDisabled ? theme.brand_0 : '',
        }}
      >
        <Icon
          src={item.icon}
          color={item.color}
          className='ml-[15px] mr-[19px] my-[11px]'
          disabled={item.isDisabled}
        />
        <Label text={title} type={LabelType.LABEL_S} color={item.color} />
      </div>
    );
  };

  const sideBarItems = useMemo(() => {
    return sideBarMenus.map((group) => {
      return {
        ...group,
        items: group.items.map((item) => {
          const isSelected = getIsItemSelected(currentLocation.pathname, item.path);
          let isDisabled = false;
          let isHidden = false;

          // Multi-view logic
          if (isMultiViewActive) {
            isDisabled =
              (auth.role === USER_ROLE.SUPPORT &&
                ![RoutePath.CHARGERS, RoutePath.REPORTS].includes(item.path)) ||
              (auth.role === USER_ROLE.NORMAL &&
                ![RoutePath.OVERVIEW, RoutePath.CHARGERS, RoutePath.REPORTS].includes(item.path));
          }

          // Disable tabs for non-support users during pending onboarding
          // Only allow Company and Chargers tabs
          if (
            !isDisabled &&
            isPendingOnboarding &&
            auth.role !== USER_ROLE.SUPPORT &&
            ![RoutePath.COMPANY, RoutePath.CHARGERS].includes(item.path)
          ) {
            isDisabled = true;
          }

          // Disable sidebar items when user lacks route permission
          if (!isHidden && companyId && companyId.toUpperCase() !== 'ALL') {
            if (
              (item.path === RoutePath.PRICING || item.path === RoutePath.PRICING_EDIT) &&
              !canViewPricing
            ) {
              isHidden = true;
            }
            if (item.path === RoutePath.PAYOUT && !canViewFinances) {
              isHidden = true;
            }
            if (item.path === RoutePath.POWER && !canViewPowerManagement) {
              isHidden = true;
            }
            if (item.path === RoutePath.VEHICLES && !canViewVehicles) {
              isHidden = true;
            }
            if (item.path === RoutePath.RFID && !canViewRfidCards) {
              isHidden = true;
            }
            if (item.path === RoutePath.ACCESS && !canViewAccessRules) {
              isHidden = true;
            }
          }

          const color = isSelected
            ? ColorType.BLACK
            : isDisabled
            ? ColorType.GREY3
            : ColorType.GREY6;
          return {
            ...item,
            isSelected,
            isDisabled,
            isHidden,
            color,
          };
        }),
      };
    });
  }, [
    currentLocation.pathname,
    isMultiViewActive,
    isPendingOnboarding,
    auth.role,
    companyId,
    canViewPricing,
    canViewFinances,
    canViewPowerManagement,
    canViewVehicles,
    canViewRfidCards,
    canViewAccessRules,
  ]);

  const renderMenu = () => {
    return sideBarItems.map((group) => {
      if (group.role && group.role !== auth.role) {
        return null;
      }
      const divs = [];
      if (group.header) {
        divs.push(renderHeader(group.header));
      }
      group.items.forEach((item) => {
        if (item.isHidden) {
          return;
        }
        if (item.isDisabled) {
          const isDisabledDueToOnboarding =
            isPendingOnboarding &&
            auth.role !== USER_ROLE.SUPPORT &&
            ![RoutePath.COMPANY, RoutePath.CHARGERS].includes(item.path);
          divs.push(getItemWithTooltip(renderItem(item), item.path, isDisabledDueToOnboarding));
        } else {
          if (checkIfSupportRoute(auth.role, item.role)) {
            divs.push(renderItem(item));
          }
        }
      });
      return <div key={`sidebar-group-${group.header}`}>{divs}</div>;
    });
  };

  const noCompanyrenderMenu = () =>
    noCompanyMenu.map((group) => {
      const divs: any = [];

      group.items.forEach((item) => {
        divs.push(renderItem(item));
      });
      return <div key={`no-company-group-${group.items[0].path}`}>{divs}</div>;
    });

  const displayMenu = () => {
    // user has multi company access
    if (companies && companies?.length > 1) {
      return (
        <div className='divide-y divide-grey2'>
          <div className='pb-5'>
            <CompanySelect
              placement='bottom-start'
              buttonProps={{
                iconRightAlign: true,
                buttonStyle: {
                  height: '48px',
                  width: '200px',
                },
                topLeftLabel: t('company').toUpperCase(),
              }}
            />
          </div>
          <div className='pt-5 pb-5'>{renderMenu()}</div>
        </div>
      );
    }
    // user has single company access
    if (companies?.length === 1) {
      return <div className='pb-5'>{renderMenu()}</div>;
    }
    // no company
    return <div>{noCompanyrenderMenu()}</div>;
  };

  return (
    <div className='w-60 bg-white block h-[calc(100vh)] overflow-x-hidden'>
      <Scrollable>
        <div className='pl-[32px] pr-[32px]'>{renderLogo()}</div>
        <div className='pl-5 pr-5'>{displayMenu()}</div>
      </Scrollable>
    </div>
  );
};
